// @flow

import { PARTICIPANT_ID_CHANGED } from '../../../../react/features/base/participants/actionTypes';
import ReducerRegistry from '../../../../react/features/base/redux/ReducerRegistry';
import {
    UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION
} from '../../../../react/features/large-video/actionTypes';

import {
    WIZY_ACTIVE_SPEAKER_ADDED, WIZY_ACTIVE_SPEAKER_REMOVED,
    WIZY_PINNED_PARTICIPANT,
    WIZY_SELECT_LARGE_VIDEO_PARTICIPANT
} from './actionTypes';

const DEFAULT_STATE = {
    participantIds: [],
    remoteScreenShares: [],
    wizySpeakersList: new Set()
};

ReducerRegistry.register('wizyconf/large-video', (state = DEFAULT_STATE, action) => {

    if (!APP.wildix.wizy) {
        return state;
    }

    const arr = [ ...state.participantIds ];

    switch (action.type) {
    case PARTICIPANT_ID_CHANGED:
        if (action.oldValue) {
            const idx = arr.findIndex(id => id === action.oldValue);

            if (idx >= 0) {
                arr.splice(idx, 1, action.newValue);

                return {
                    ...state,
                    participantIds: arr
                };
            }
        }

        return state;

    case WIZY_SELECT_LARGE_VIDEO_PARTICIPANT: {
        if (JSON.stringify(state.remoteScreenShares) === JSON.stringify(action.remoteScreenShares)
            && action.participantIds.length === arr.filter(Boolean).length
            && action.participantIds.every(id => arr.includes(id))) {
            return state;
        }

        const participantIds = APP.wildix.wizy.screenKeys.length === 1
            ? action.participantIds
            : sortParticipantIdsByScreens(arr, action.participantIds, action.remoteScreenShares);

        return {
            ...state,
            participantIds,
            remoteScreenShares: action.remoteScreenShares
        };
    }

    case UPDATE_KNOWN_LARGE_VIDEO_RESOLUTION:
        return {
            ...state,
            resolution: action.resolution
        };

    case WIZY_PINNED_PARTICIPANT:
        return {
            ...state,
            wizyPinnedParticipant: action.payload
        };

    case WIZY_ACTIVE_SPEAKER_ADDED:
        state.wizySpeakersList.has(action.speaker) && state.wizySpeakersList.delete(action.speaker);
        state.wizySpeakersList.add(action.speaker);

        return {
            ...state
        };

    case WIZY_ACTIVE_SPEAKER_REMOVED:
        if (state.wizySpeakersList.delete(action.speaker)) {
            return {
                ...state
            };
        }
    }

    return state;
});

/**
 * Sort the participant identifiers list based on available screens.
 *
 * @param {array} oldIds - List of participant identifiers from the state.
 * @param {array} newIds - List of participant identifiers to be added to screens.
 * @param {array} newScreenShares - List of all actual screen shares in the room.
 */
function sortParticipantIdsByScreens(oldIds, newIds, newScreenShares) {
    const screensMap = new Map();
    const screenShares = new Set([ ...newScreenShares ]);
    const participantIds = new Set([ ...newIds ]);
    const screenKeys = APP.wildix.wizy.screenKeys;

    const sortedScreens = screenKeys.map((key, index) => [ key, oldIds[index] ])
        .sort(([ key1 ], [ key2 ]) => key1.localeCompare(key2));

    sortedScreens.forEach(([ screenId, participantId ], index) => {
        const electedParticipant = electParticipantForScreen(participantId, index, sortedScreens, screenShares, participantIds, oldIds);

        screenShares.has(electedParticipant) && screenShares.delete(electedParticipant);
        participantIds.has(electedParticipant) && participantIds.delete(electedParticipant);

        screensMap.set(screenId, electedParticipant);
    });

    return screenKeys.map(key => screensMap.get(key));
}

/**
 * Returns the identifier of the participant who is to be on the screen.
 *
 * @param {string|null} currentId - The identifier of the participant who is on the screen.
 * @param {number} screenIndex - Screen priority identifier.
 * @param {array} sortedScreens - List of sorted screens by priority.
 * @param {array} screenShares - List of all actual screen shares in the room sorted from newer to older.
 * @param {array} newIds - List of participant identifiers to be added to screens.
 * @param {array} oldIds - List of participant identifiers from the state.
 * @returns {null|*}
 */
function electParticipantForScreen(currentId, screenIndex, sortedScreens, screenShares, newIds, oldIds) {
    // if there are more available screens than the number of participants in the room, leave the screen blank
    if (screenShares.size === 0 && newIds.size === 0) {
        return null;
    }

    // First, pick the screen share that was on the screen or the most recent available remote screen share
    if (screenShares.size) {
        const screenShareId = screenShares.has(currentId)
            ? currentId
            : screenShares.values().next().value;

        // Don't set screen sharing if:
        // it was set on a lower priority screen (the screen not first)
        // or is not present in the new participant ids list,
        // or it's a new screen sharing and current participant should be on a screen
        if (
            newIds.has(screenShareId)
            && !(
                screenIndex !== 0
                && screenShareId !== currentId
                && (oldIds.includes(screenShareId) || newIds.has(currentId))
            )
        ) {
            return screenShareId;
        }
    }

    // Next, pick the participant that was set on the screen
    if (newIds.has(currentId)) {
        return currentId;
    }

    // Next, pick the participant that wasn't set on a lower priority screen
    if (newIds.size > 1) {
        const remainingScreens = sortedScreens.slice(screenIndex);

        return Array.from(newIds.values()).find(pId => !remainingScreens.find(([ , rsId ]) => pId === rsId));
    }

    return newIds.values().next().value;
}
